import React, { Component } from "react";
import styled from "styled-components";
import { defineMessages, FormattedMessage } from "react-intl";
import FileIcon from "../../icons/file";
import { BASE_URL } from "../../request";
import { HttpClient } from "../../../../lib/http";
import { regenPropertyCache } from "../../../common/api";
import { createFileDownloadFromRawData } from "../../../../lib/supportFunctions";
import { format } from "date-fns";

const messages = defineMessages({
  download: {
    id: "app.oneTimePromoCode.download",
    description: "Descargar la plantilla de códigos promocionales de uso único",
    defaultMessage: "Descargar la plantilla de códigos promocionales de uso único",
  },
  promoCodes: {
    id: "app.oneTimePromoCode.promoCodes",
    description: "Códigos promocionales",
    defaultMessage: "Códigos promocionales",
  },
  upload: {
    id: "app.oneTimePromoCode.upload",
    description: "Cargar la plantilla con los códigos nuevos",
    defaultMessage: "Cargar la plantilla con los códigos nuevos",
  },
  chooseFile: {
    id: "app.oneTimePromoCode.chooseFile",
    description: "Elegir archivo",
    defaultMessage: "Elegir archivo",
  },
  buttonInfo: {
    id: "app.oneTimePromoCode.buttonInfo",
    description: "El archivo subido sustituirá el anterior.",
    defaultMessage: "El archivo subido sustituirá el anterior.",
  },
  uploadFailed: {
    id: "app.oneTimePromoCode.uploadFailed",
    description: "Upload failed.",
    defaultMessage: "Upload failed.",
  },
  reviewFollowingPoints: {
    id: "app.oneTimePromoCode.reviewFollowingPoints",
    description: "Review the following points",
    defaultMessage: "Review the following points",
  },
  cannotHaveSpecialCharacters: {
    id: "app.oneTimePromoCode.cannotHaveSpecialCharacters",
    description: "Promotional codes cannot have special characters and / or spaces.",
    defaultMessage: "Promotional codes cannot have special characters and / or spaces.",
  },
  expirationDate: {
    id: "app.oneTimePromoCode.expirationDate",
    description: "Promotional codes without expiration date or expiration date expired.",
    defaultMessage: "Promotional codes without expiration date or expiration date expired.",
  },
  dateFormat: {
    id: "app.oneTimePromoCode.dateFormat",
    description: "The date format must be YYYY-MM-DD.",
    defaultMessage: "The date format must be YYYY-MM-DD.",
  },
});

const StyledContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 15px;
  ${props => (props.disabled ? `pointer-events: none;` : ``)}
`;

const StyledDownloadContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  align-items: center;
  margin-bottom: 30px;
`;

const StyledUploadContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  align-items: baseline;
`;

const StyledDescription = styled.div`
  flex: 0 1 auto;
  margin-right: 15px;
`;

const StyledLink = styled.a`
  cursor: pointer;
`;

const StyledExcelContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 0 1 auto;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: 20px;
`;

const StyledButtonContainer = styled.div`
  flex: 0 1 auto;
  width: 100%;
`;

const StyledLabel = styled.label`
  padding: 5px 30px;
  background-color: #5d9bec;
  color: #fff;
  border-radius: 5px;
  cursor: pointer;
`;

const StyledButton = styled.input`
  margin: 10px 0;
  color: transparent;
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
`;

const StyledHelpText = styled.div`
  flex: 0 1 auto;
  width: 100%;
  font-size: 12px;
`;

const StyledLoader = styled.div`
  border: 3px solid #f3f3f3; /* Light grey */
  border-top: 3px solid #3498db; /* Blue */
  border-bottom: 3px solid #3498db; /* Blue */
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 2s linear infinite;
`;

const StyledStatusResponse = styled.div`
  margin-top: 5px;
  padding: 3px 5px;
  border-radius: 3px;
  font-size: 11px;

  ${props => props.success && "color: #ffffff; background-color: #27C24C;font-weight: 600;"};
  ${props => props.failure && "color: #ab514c; background-color: #eedfde;"};
`;

class OneTimePromoCode extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      success: false,
      failure: false,
    };

    this.apiClient = new HttpClient(BASE_URL);
  }

  getPropertyTickersFiltered = ({ propertyTickers, appliesToInventoriesList }) => {
    return propertyTickers.reduce((result, ticker, index) => {
      if (appliesToInventoriesList[index] && appliesToInventoriesList[index].length) {
        result = [...result, ticker];
      }
      return result;
    }, []);
  };

  upload = async event => {
    console.info(this.props.field);
    const { propertyTicker, ticker: promoTicker, appliesToInventoriesList } = this.props.formValues;
    const { propertyTickers, entityType } = this.props.field;
    const propertyTickersFiltered = this.getPropertyTickersFiltered({
      propertyTickers,
      appliesToInventoriesList,
    });
    console.info(propertyTickersFiltered);
    this.setState({ loading: true, failure: false });
    const dataToSend = new FormData();
    dataToSend.append("file", event.target.files[0]);
    if (Array.isArray(propertyTickersFiltered) && propertyTickersFiltered.length) {
      dataToSend.append("properties", propertyTickersFiltered);
    }

    try {
      await this.apiClient.post(
        `/api/backoffice/${propertyTicker}/onetimepromocodes/${promoTicker}/type/${entityType}`,
        dataToSend
      );

      this.setState({ loading: false, success: true, failure: false });
      regenPropertyCache(this.apiClient, propertyTicker).then();
    } catch (error) {
      this.setState({ loading: false, success: false, failure: true });
    }
  };

  getOneTimePromoCodesExcel = async (propertyTicker, promoTicker) => {
    try {
      const response = await this.apiClient.get(
        `/api/backoffice/${propertyTicker}/onetimepromocodes/${promoTicker}`,
        {
          responseType: "blob",
        }
      );
      createFileDownloadFromRawData(
        response,
        `onetimepromocodes_${format(new Date(), "yyyyMMdd")}`,
        "application/vnd.openxmlformats",
        "xlsx"
      );

      this.setState({ loading: false, failure: false });
    } catch (error) {
      console.error("Error al descargar el archivo:", error);
      this.setState({ loading: false, success: false, failure: true });
    }
  };

  render() {
    const { propertyTicker, ticker: promoTicker } = this.props.formValues;
    const { loading, success, failure } = this.state;
    const { disabled } = this.props;
    return (
      <StyledContainer disabled={disabled}>
        <StyledDownloadContainer>
          <StyledDescription>
            <FormattedMessage {...messages.download} />:
          </StyledDescription>

          <StyledExcelContainer>
            <FileIcon />

            <StyledLink
              href="#"
              id="excelUniqueCodes"
              onClick={e => this.getOneTimePromoCodesExcel(propertyTicker, promoTicker)}>
              <FormattedMessage {...messages.promoCodes} />
            </StyledLink>
          </StyledExcelContainer>
        </StyledDownloadContainer>

        <StyledUploadContainer>
          <FormattedMessage {...messages.upload} />:
          <StyledButtonWrapper>
            {!loading && (
              <StyledButtonContainer>
                <StyledLabel htmlFor="excelInput">
                  <FormattedMessage {...messages.chooseFile} />
                </StyledLabel>
                <StyledButton
                  id="excelInput"
                  type="file"
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  formEncType="multipart/form-data"
                  onChange={this.upload}
                />
              </StyledButtonContainer>
            )}

            {loading && <StyledLoader />}
            <StyledHelpText>
              <FormattedMessage {...messages.buttonInfo} />
            </StyledHelpText>

            {success && (
              <StyledStatusResponse success={success}>Archivo subido!</StyledStatusResponse>
            )}

            {failure && (
              <StyledStatusResponse failure={failure}>
                <ul>
                  <FormattedMessage {...messages.uploadFailed} />
                  <FormattedMessage {...messages.reviewFollowingPoints} />:
                  <li>
                    <FormattedMessage {...messages.cannotHaveSpecialCharacters} />
                  </li>
                  <li>
                    <FormattedMessage {...messages.expirationDate} />
                  </li>
                  <li>
                    <FormattedMessage {...messages.dateFormat} />
                  </li>
                </ul>
              </StyledStatusResponse>
            )}
          </StyledButtonWrapper>
        </StyledUploadContainer>
      </StyledContainer>
    );
  }
}

export default OneTimePromoCode;
